/**
 * WordPress dependencies
 */
import { useSelect } from '@safe-wordpress/data';
import { _x } from '@safe-wordpress/i18n';

/**
 * External dependencies
 */
import clsx from 'clsx';
import { PostSearcher } from '@nab/components';

import type { PostSearcherProps } from '@nab/components';
import type { ExperimentEditProps } from '@nab/types';

// NOTE. Dependency loop with @nab package.
import type { store as editorStore } from '@nab/editor';
const NAB_EDITOR = 'nab/editor' as unknown as typeof editorStore;

/**
 * Internal dependencies
 */
import './style.scss';

import type { ControlAttributes } from '../../types';
import { CheckboxControl } from '@safe-wordpress/components';

export type OriginalProps = ExperimentEditProps< ControlAttributes > & {
	readonly validPostFilter?: PostSearcherProps[ 'filter' ];
};

export const Original = ( {
	attributes,
	setAttributes,
	validPostFilter,
}: OriginalProps ): JSX.Element => {
	const canPriceTestingBeEnabled = useCanPriceTestingBeEnabled();

	return (
		<div className="nab-original-product-alternative">
			<div className="nab-original-product-alternative__selector">
				<PostSearcher
					type="product"
					value={ attributes.postId }
					perPage={ 10 }
					onChange={ ( value ) =>
						setAttributes( {
							// @ts-expect-error Product IDs are always numbers.
							postId: value,
							postType: 'product',
						} )
					}
					filter={ validPostFilter }
					placeholder={ _x(
						'Please select a product…',
						'user',
						'nelio-ab-testing'
					) }
				/>
			</div>
			<div className="nab-original-product-alternative__flags">
				<CheckboxControl
					className={ clsx( {
						'nab-original-product-alternative__flag': true,
						'nab-original-product-alternative__flag--is-disabled':
							! canPriceTestingBeEnabled,
					} ) }
					checked={
						canPriceTestingBeEnabled &&
						! attributes.disablePriceTesting
					}
					disabled={ ! canPriceTestingBeEnabled }
					onChange={ ( enabled ) =>
						setAttributes( {
							disablePriceTesting: ! enabled ? true : undefined,
						} )
					}
					label={ _x(
						'Enable price testing',
						'command',
						'nelio-ab-testing'
					) }
				/>
			</div>
		</div>
	);
};

// =====
// HOOKS
// =====

const useCanPriceTestingBeEnabled = () =>
	useSelect( ( select ) => {
		const scope = select( NAB_EDITOR ).getScope();
		return 'tested-post' !== scope[ 0 ]?.attributes.type;
	}, [] );
